import { StyledMobileMapFilterPanel } from './mobileStyles';
import Button from 'components/Button/Button';
import useDevice from 'hooks/useDevice';
import { Content, Horizontal, Vertical, VerticalSpacer } from 'gls';
import { style } from 'typestyle';
import { colours } from 'styling/colours';
import { useMapStore, useUserRolePermissionStore } from 'contexts/RootStore.context';
import { observer } from 'mobx-react';
import { CheckBox } from 'components/inputs/CheckBox/CheckBox';
import MultiSelectCheckbox from 'components/inputs/Select/MultiSelectCheckbox';
import useMap from 'views/map/hooks/useMap';
import { MapContainerRefContext } from 'views/map/utils/mapContainerRef.context';
import { useContext, useMemo, useState } from 'react';
import { KeyValuePair } from 'utils/common';
import { icons } from 'assets/icons/index'
import { SearchInput } from 'components/SearchInput/SearchInput';
import { AssetPermissions } from 'views/config/roles/RoleConfigDto';


type MobileFilterPanelProps = {
  isOpen: boolean
  onClose: () => void;
}
export const MobileFilterPanel = observer(({ onClose, isOpen }: MobileFilterPanelProps) => {
  const { isMobileDevice } = useDevice();
  const { filterForm, loadAssets, assetStatuses, selectBeacon } = useMapStore()
  const { getAssetTypes, getAssetOwners } = useUserRolePermissionStore()
  const { fitMapToFilterResults } = useMap(useMapStore())
  const { map } = useContext(MapContainerRefContext)
  const [emptyError, setEmptyError] = useState(false)

  const statusOptions: KeyValuePair[] = useMemo(() => {
    const options = assetStatuses.map(r => ({ key: r.description, value: r.asset_status_id }))
    options.push({ key: "No status set", value: "__null" })
    return options;
  }, [assetStatuses])

  const { asset_types, asset_owners } = useMemo(() => {
    return {
      asset_types: getAssetTypes(AssetPermissions.view),
      asset_owners: getAssetOwners(AssetPermissions.view)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const EmptyFilters = () => {
    if (
      filterForm.$.searchTerm.value === ""
      && filterForm.$.assetTypes.value.length === asset_types.length
      && filterForm.$.owners.value.length === asset_owners.length
      && filterForm.$.assetStatuses.value.length === statusOptions.length
    )
      return true
  }

  const filterAssets = () => {
    filterForm.$.searchIn.onChange("assets")
    setEmptyError(false)
    loadAssets().then(items => {
      selectBeacon(null)
      if (items.length === 0 && !EmptyFilters()) {
        setEmptyError(true)
        return
      }
      map && fitMapToFilterResults(map, items)
      onClose()
    })
  }

  return (
    <StyledMobileMapFilterPanel
      title="Search & Filter"
      from="left"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <Vertical spacing={0} sizing="stretch">
        <span className={titleStyle}>Search</span>
        <Content className={headerStyle}>
          <SearchInput
            width='100%'
            placeHolder='Search assets'
            value={filterForm.$.searchTerm.value}
            onChange={(value) => {
              setEmptyError(false)
              filterForm.$.searchTerm.onChange(value)
            }}
          />
          {emptyError && filterForm.$.searchTerm.value !== "" && (
            <Horizontal className={ErrorStyle} spacing={5}>
              <icons.ErrorIcon aria-label="Error" />
              <span>Sorry, no assets were found for {filterForm.$.searchTerm.value}</span>
            </Horizontal>
          )}
        </Content>
        {!isMobileDevice && (
          <>
            <span className={titleStyle}>Show</span>
            <Vertical className={headerStyle}>
              <CheckBox
                id="show-assets-checkbox"
                label='Assets'
                checked={filterForm.$.show.value.includes("assets")}
                onChange={(e) => {
                  if (e.target.checked)
                    filterForm.$.show.onChange([...filterForm.$.show.value, "assets"])
                  else {
                    filterForm.$.show.onChange(
                      filterForm.$.show.value.length === 1 ? ["users"] : filterForm.$.show.value.filter(v => v !== "assets")
                    )
                  }
                }}
                name='show-assets'
                value='1'
              />
              <CheckBox
                id="show-users-checkbox"
                label='People'
                checked={filterForm.$.show.value.includes("users")}
                onChange={(e) => {
                  if (e.target.checked)
                    filterForm.$.show.onChange([
                      ...filterForm.$.show.value.filter(v => v !== "devices"),
                      "users"])
                  else
                    filterForm.$.show.onChange(["assets"])
                }}
                name='show-users'
                value='2'
              />
              <CheckBox
                id="show-devices-checkbox"
                label='Devices'
                checked={filterForm.$.show.value.includes("devices")}
                onChange={(e) => {
                  if (e.target.checked)
                    filterForm.$.show.onChange([
                      ...filterForm.$.show.value.filter(v => v !== "users"),
                      "devices"])
                  else
                    filterForm.$.show.onChange(["assets"])
                }}
                name='show-devices'
                value='3'
              />
            </Vertical>
          </>
        )}
        <span className={titleStyle}>Filter</span>
        <Vertical className={headerStyle} spacing={10}>
          <MultiSelectCheckbox
            includeAll
            id='asset-types-select'
            isClearable={false}
            selectedValuePrefix='Asset Type: '
            options={asset_types.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.assetTypes.value}
            onChange={filterForm.$.assetTypes.onChange}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Asset Type: (0)'
            className={filterComponentStyle}
            height='40px'
            width='100%'
          />
          <MultiSelectCheckbox
            id='owners-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Owners: '
            placeholder='Owners: (0)'
            options={asset_owners.map(r => ({ key: r.label, value: r.value }))}
            value={filterForm.$.owners.value}
            onChange={filterForm.$.owners.onChange}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            className={filterComponentStyle}
            height='40px'
            width='100%'
          />
          <MultiSelectCheckbox
            id='asset-statuses-select'
            includeAll
            isClearable={false}
            selectedValuePrefix='Status: '
            options={statusOptions}
            value={filterForm.$.assetStatuses.value}
            onChange={filterForm.$.assetStatuses.onChange}
            selectedTextRender={(values, options) =>
              `${options.length === values.length ? "All " : ""}(${values.filter(r => r.value !== "__all").length})`}
            placeholder='Status: (0)'
            className={filterComponentStyle}
            height='40px'
            width='100%'
          />
        </Vertical>
        {emptyError && filterForm.$.searchTerm.value === "" && (
          <Horizontal className={ErrorStyle2} spacing={5}>
            <icons.ErrorIcon aria-label="Error" />
            <span>Sorry, no assets were found using these filters</span>
          </Horizontal>
        )}
        <VerticalSpacer />
        <Button id="mobile-filter-button" onClick={filterAssets}>Done</Button>
      </Vertical>

    </StyledMobileMapFilterPanel>
  );
})

const titleStyle = style({
  width: '100%',
  textAlign: "center",
  fontWeight: 'bold',
  color: colours.secondaryText,
  height: '50px',
  paddingTop: '18px'
})

const filterComponentStyle = style({
  width: "100%",
  maxWidth: '100% !important'
})

export const headerStyle = style({
  width: '100%',
  position: "relative",
  padding: "10px",
  backgroundColor: colours.panelBackground,
})

const ErrorStyle = style({
  position: "absolute",
  top: '50px',
  backgroundColor: colours.white,
  color: colours.error,
  width: '100%',
  padding: "10px",
  border: `1px solid ${colours.border}`
})

const ErrorStyle2 = style({
  backgroundColor: colours.white,
  color: colours.error,
  width: '100%',
  padding: "10px",
  border: `1px solid ${colours.border}`
})